import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from '../components/AdminShell';
import { BrandsPanel } from '../admin/BrandsPanel';
import { CompaniesPanel } from '../admin/CompaniesPanel';
import { ImportPanel } from '../admin/ImportPanel';
import { UsersPanel } from '../admin/UsersPanel';

type Tab = 'import' | 'brands' | 'companies' | 'users';

const TABS: Tab[] = ['import', 'brands', 'companies', 'users'];

/**
 * Pannello di amministrazione, accessibile al solo ruolo admin (contratto §2).
 *
 * Non esiste nell'Allegato A: la sua "Impostazioni" è una voce di menu senza
 * schermata. È costruito con i token del design system del riferimento, senza
 * introdurre un secondo linguaggio visivo.
 */
export function AdminPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('import');

  return (
    <AdminShell onBack={() => void navigate('/')}>
      <div className="admin-page">
        <header className="admin-header">
          <h1>{t('admin.title')}</h1>
          <p>{t('admin.subtitle')}</p>
        </header>

        <nav className="admin-tabs">
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              className={name === tab ? 'admin-tab active' : 'admin-tab'}
              onClick={() => setTab(name)}
            >
              {t(`admin.tabs.${name}`)}
            </button>
          ))}
        </nav>

        {/*
          Il caricamento dati resta montato e si nasconde, invece di smontarsi
          come le altre tre schede: è l'unica con del lavoro in corso da
          perdere. Smontandolo si perdevano il JSON incollato e il file scelto
          (feedback 23/10, punto 15), e lo stato del campo file nemmeno si
          potrebbe ripristinare da codice: il valore di un `input type=file`
          lo scrive solo l'utente. Il modulo si svuota al suo "Svuota".
        */}
        <div hidden={tab !== 'import'}>
          <ImportPanel />
        </div>
        {tab === 'brands' ? <BrandsPanel /> : null}
        {tab === 'companies' ? <CompaniesPanel /> : null}
        {tab === 'users' ? <UsersPanel /> : null}
      </div>
    </AdminShell>
  );
}
